import { useState, useEffect } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { setClientIconUrl } from '../../../utils/clientIcon';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const useSplash = (showMessage) => {
  const [splashInfo, setSplashInfo] = useState(null);
  const [loadingSplash, setLoadingSplash] = useState(false);
  const [uploadingSplash, setUploadingSplash] = useState(false);
  const [splashUrlInput, setSplashUrlInput] = useState('');
  const [splashFile, setSplashFile] = useState(null);
  const [splashPreviewUrl, setSplashPreviewUrl] = useState(null);
  const [splashActive, setSplashActive] = useState(true);
  const [iconUrlInput, setIconUrlInput] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(splashPreviewUrl);
      }
    };
  }, [splashPreviewUrl]);

  useEffect(() => {
    return () => {
      if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
    };
  }, [iconPreviewUrl]);

  const resetSplashState = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setSplashInfo(null);
    setSplashUrlInput('');
    setSplashFile(null);
    setSplashPreviewUrl(null);
    setSplashActive(true);
    setIconUrlInput('');
    setIconFile(null);
    setIconPreviewUrl(null);
  };

  const fetchSplashData = async (clientName) => {
    if (!clientName) {
      resetSplashState();
      return;
    }

    try {
      setLoadingSplash(true);
      const response = await apiFetch(
        `${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`,
        { method: 'GET' }
      );

      if (response.ok) {
        const json = await response.json();
        const record = json?.data || null;
        setSplashInfo(record);
        setSplashActive(record?.isActive !== false);
        const urlValue = record?.fullUrl || record?.gifUrl || '';
        setSplashUrlInput(urlValue);
        setSplashPreviewUrl(urlValue || null);
        const iconValue = record?.iconFullUrl || record?.iconUrl || '';
        setIconUrlInput(iconValue);
        setIconPreviewUrl(iconValue || null);
        setIconFile(null);
      } else if (response.status === 404) {
        resetSplashState();
      } else {
        throw new Error('Unable to load splash screen details');
      }
    } catch (error) {
      console.error('Failed to fetch splash screen:', error);
      showMessage('error', 'Failed to load splash screen');
    } finally {
      setLoadingSplash(false);
      setSplashFile(null);
      setIconFile(null);
    }
  };

  const handleSplashFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.type !== 'image/gif') {
      showMessage('error', 'Only GIF files are allowed');
      event.target.value = '';
      return;
    }

    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setSplashFile(file);
    setSplashPreviewUrl(preview);
    setSplashUrlInput('');
  };

  const clearSplashFile = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    setSplashFile(null);
    setSplashPreviewUrl(splashInfo?.fullUrl || splashInfo?.gifUrl || null);
    setSplashUrlInput(splashInfo?.fullUrl || splashInfo?.gifUrl || '');
  };

  const handleIconFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Icon must be PNG, JPG, JPEG, SVG, or WEBP');
      event.target.value = '';
      return;
    }

    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setIconFile(file);
    setIconPreviewUrl(preview);
    setIconUrlInput('');
  };

  const clearIconFile = () => {
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconFile(null);
    const fallback = splashInfo?.iconFullUrl || splashInfo?.iconUrl || '';
    setIconPreviewUrl(fallback || null);
    setIconUrlInput(fallback);
  };

  const handleSplashSave = async (selectedCompany) => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    const trimmedGifUrl = splashUrlInput.trim();
    const trimmedIconUrl = iconUrlInput.trim();

    if (!splashFile && !trimmedGifUrl) {
      showMessage('error', 'Upload a GIF or provide an external URL');
      return;
    }

    try {
      setUploadingSplash(true);
      let response;
      const shouldUseFormData = Boolean(splashFile || iconFile);

      if (shouldUseFormData) {
        const formData = new FormData();
        formData.append('clientName', selectedCompany);
        formData.append('isActive', splashActive ? 'true' : 'false');
        if (splashFile) {
          formData.append('splashGif', splashFile);
        } else {
          formData.append('gifUrl', trimmedGifUrl);
        }
        if (iconFile) {
          formData.append('splashIcon', iconFile);
        } else {
          formData.append('iconUrl', trimmedIconUrl);
        }

        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientName: selectedCompany,
            gifUrl: trimmedGifUrl,
            iconUrl: trimmedIconUrl,
            isActive: splashActive,
          }),
        });
      }

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to save splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      setSplashInfo(record);
      setSplashActive(record?.isActive !== false);

      const urlValue = record?.fullUrl || record?.gifUrl || '';
      setSplashUrlInput(urlValue);
      if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(splashPreviewUrl);
      }
      setSplashPreviewUrl(urlValue || null);
      setSplashFile(null);

      const iconValue = record?.iconFullUrl || record?.iconUrl || '';
      setIconUrlInput(iconValue);
      if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
      setIconPreviewUrl(iconValue || null);
      setIconFile(null);

      const loggedClientName = localStorage.getItem('clientName');
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }

      showMessage('success', 'Splash screen saved successfully');
    } catch (error) {
      console.error('Failed to save splash screen:', error);
      showMessage('error', error.message || 'Failed to save splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const handleSplashDeactivate = async (selectedCompany) => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    try {
      setUploadingSplash(true);
      const response = await apiFetch(
        `${BASE_URL}/api/client-splash/${encodeURIComponent(selectedCompany)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to deactivate splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      setSplashInfo(record);
      setSplashActive(record?.isActive !== false);

      const urlValue = record?.fullUrl || record?.gifUrl || '';
      setSplashUrlInput(urlValue);
      setSplashPreviewUrl(urlValue || null);
      setSplashFile(null);

      const iconValue = record?.iconFullUrl || record?.iconUrl || '';
      setIconUrlInput(iconValue);
      setIconPreviewUrl(iconValue || null);
      setIconFile(null);

      const loggedClientName = localStorage.getItem('clientName');
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }

      showMessage('success', 'Splash screen deactivated');
    } catch (error) {
      console.error('Failed to deactivate splash screen:', error);
      showMessage('error', error.message || 'Failed to deactivate splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const toggleSplashActive = () => {
    setSplashActive((prev) => !prev);
  };

  return {
    splashInfo,
    loadingSplash,
    uploadingSplash,
    splashUrlInput,
    setSplashUrlInput,
    splashFile,
    splashPreviewUrl,
    splashActive,
    iconUrlInput,
    setIconUrlInput,
    iconFile,
    iconPreviewUrl,
    setIconPreviewUrl,
    resetSplashState,
    fetchSplashData,
    handleSplashFileChange,
    clearSplashFile,
    handleIconFileChange,
    clearIconFile,
    handleSplashSave,
    handleSplashDeactivate,
    toggleSplashActive,
  };
};